import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { FirebaseError } from "firebase/app";
import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { CircleAlert, CircleCheck, LoaderCircle, ShieldCheck } from "lucide-react";

import routeNames from "constants/route-names";
import { AuthorityClaimStatus } from "constants/field-values";
import { LoginStatus } from "constants/login-status";
import {
	CLAIM_EMAIL_PARAM,
	CLAIM_SETUP_PROGRESS_STEP_INTERVAL_MS,
	CLAIM_SETUP_PROGRESS_STEPS,
	CLAIM_SETUP_REDIRECT_DELAY_MS,
	ClaimSetupState
} from "constants/constants";
import { auth } from "config/firebase/firebase";
import { getFreshToken } from "config/apollo/apollo";
import { useAuth } from "context/AuthContext";
import { useCompleteAuthorityClaimMutation } from "graphql/mutations/complete-authority-claim.generated";
import ClaimJurisdictionLayout from "components/claim-jurisdiction-layout/claim-jurisdiction-layout";
import ClaimJurisdictionCard from "components/claim-jurisdiction-card/claim-jurisdiction-card";
import ClaimJurisdictionButton from "components/claim-jurisdiction-button/claim-jurisdiction-button";

const OLLIE_ANALYZING_IMAGE = `${process.env.PUBLIC_URL}/assets/images/icons/ollie-claim-analyzing.png`;

// Firebase codes of a link that cannot sign in anymore: expired, already used, or for another email.
const UNUSABLE_LINK_ERROR_CODES = ["auth/invalid-action-code", "auth/expired-action-code", "auth/invalid-email"];

const readSetupErrorMessage = (error: unknown) => {
	if (error instanceof FirebaseError && UNUSABLE_LINK_ERROR_CODES.includes(error.code)) {
		return "This link has expired or was already used. Start the claim again to get a new link.";
	}

	return (error as ApolloError).graphQLErrors?.[0]?.message || "Your account could not be set up. Please try again.";
};

/**
 * /claim/setup: the screen the email link opens.
 * 1. Signs the person in with the link. The email comes in the link, so it works on any device.
 * 2. Calls complete_authority_claim, which creates the account in the Authority organization.
 * 3. Shows the checklist. Every row completes after CLAIM_SETUP_PROGRESS_STEP_INTERVAL_MS, and
 *    the last row waits for the account. Then the dashboard opens.
 * A claim picked by hand from the list ends in the review message instead of the dashboard.
 */
const ClaimJurisdictionSetup = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { loginStatus, logout, updateToken } = useAuth();

	const [setupState, setSetupState] = useState(ClaimSetupState.PREPARING);
	const [completedStepCount, setCompletedStepCount] = useState(0);
	const [jurisdictionName, setJurisdictionName] = useState("");
	const [errorMessage, setErrorMessage] = useState("");

	const hasStartedRef = useRef(false);
	// Read by the checklist timer, which must not restart when the account is ready.
	const isAccountReadyRef = useRef(false);

	// errorPolicy "none" makes the promise reject on a GraphQL error, so the catch shows the message
	// of the service (for example an account that already exists) instead of a generic one.
	const [completeAuthorityClaim] = useCompleteAuthorityClaimMutation({ errorPolicy: "none" });

	const showFailure = (message: string) => {
		setErrorMessage(message);
		setSetupState(ClaimSetupState.FAILED);
	};

	const signInWithClaimLink = async () => {
		const claimEmail = searchParams.get(CLAIM_EMAIL_PARAM) || "";
		const pageUrl = window.location.href;

		if (!isSignInWithEmailLink(auth, pageUrl)) return;

		// Another account signed in on this device must not keep its cached data in the new session.
		const isOtherUserSignedIn =
			!!auth.currentUser && auth.currentUser.email?.toLowerCase() !== claimEmail.toLowerCase();
		if (isOtherUserSignedIn && logout) {
			await logout(false);
		}

		await signInWithEmailLink(auth, claimEmail, pageUrl);

		// The one-time code in the address is spent. Removing it keeps a reload from trying it again.
		navigate(routeNames.CLAIM_JURISDICTION_SETUP, { replace: true });
	};

	const startSetup = async () => {
		try {
			await signInWithClaimLink();

			if (!auth.currentUser) {
				showFailure("Open the link from your email to continue.");
				return;
			}

			// The action reads the email from this token, so it must belong to the user just signed in.
			await getFreshToken({ forceRefresh: true });

			const { data } = await completeAuthorityClaim();
			const claimResult = data?.complete_authority_claim;
			setJurisdictionName(claimResult?.jurisdiction_name || "");

			if (claimResult?.status === AuthorityClaimStatus.COMPLETED) {
				// The new role and organization reach the app with a fresh token and an empty cache.
				if (updateToken) {
					await updateToken(auth.currentUser);
				}
				isAccountReadyRef.current = true;
			} else if (claimResult?.status === AuthorityClaimStatus.PENDING_REVIEW) {
				setSetupState(ClaimSetupState.UNDER_REVIEW);
			} else if (claimResult?.status === AuthorityClaimStatus.REJECTED) {
				setSetupState(ClaimSetupState.REJECTED);
			} else {
				showFailure("Your account could not be set up. Please try again.");
			}
		} catch (error) {
			console.log("ClaimJurisdictionSetup startSetup error: ", error);
			showFailure(readSetupErrorMessage(error));
		}
	};

	// Starts once Firebase knows whether somebody is already signed in on this device.
	useEffect(() => {
		if (loginStatus === LoginStatus.LOADING || hasStartedRef.current) return;

		hasStartedRef.current = true;
		startSetup();
	}, [loginStatus]);

	// Completes one checklist row at a time. The last row stays in progress until the account is ready.
	useEffect(() => {
		if (setupState !== ClaimSetupState.PREPARING) return;

		const progressInterval = window.setInterval(() => {
			const lastCompletableCount = isAccountReadyRef.current
				? CLAIM_SETUP_PROGRESS_STEPS.length
				: CLAIM_SETUP_PROGRESS_STEPS.length - 1;
			setCompletedStepCount(count => Math.min(count + 1, lastCompletableCount));
		}, CLAIM_SETUP_PROGRESS_STEP_INTERVAL_MS);

		return () => window.clearInterval(progressInterval);
	}, [setupState]);

	// Opens the dashboard a moment after the last row is checked.
	useEffect(() => {
		if (completedStepCount < CLAIM_SETUP_PROGRESS_STEPS.length) return;

		const redirectTimeout = window.setTimeout(() => {
			navigate(routeNames.DASHBOARD, { replace: true });
		}, CLAIM_SETUP_REDIRECT_DELAY_MS);

		return () => window.clearTimeout(redirectTimeout);
	}, [completedStepCount]);

	const progressRows = CLAIM_SETUP_PROGRESS_STEPS.map((stepText, index) => {
		const isCompleted = index < completedStepCount;
		const isInProgress = index === completedStepCount;

		let stepIcon = <span className="claim-jurisdiction-setup-step-pending-icon" />;
		if (isCompleted) {
			stepIcon = <CircleCheck size={20} className="claim-jurisdiction-setup-step-icon" />;
		} else if (isInProgress) {
			stepIcon = <LoaderCircle size={20} className="claim-jurisdiction-setup-step-icon claim-jurisdiction-setup-spinner" />;
		}

		const isReached = isCompleted || isInProgress;

		return (
			<div
				key={stepText}
				className={`claim-jurisdiction-setup-step ${isReached ? "" : "claim-jurisdiction-setup-step-upcoming"}`}
			>
				{stepIcon}
				{stepText}
			</div>
		);
	});

	const preparingCard = (
		<ClaimJurisdictionCard
			key={ClaimSetupState.PREPARING}
			eyebrowIcon={<LoaderCircle size={16} className="claim-jurisdiction-setup-spinner" />}
			eyebrow="SETTING UP YOUR SPACE"
			title="We’re getting everything in order."
			description="Ollie is preparing your workspace. You’ll automatically be redirected to your dashboard when it’s ready."
		>
			<div className="claim-jurisdiction-setup-progress" role="status" aria-live="polite">
				<div className="claim-jurisdiction-setup-ollie">
					<img src={OLLIE_ANALYZING_IMAGE} alt="Ollie reviewing plans" className="claim-jurisdiction-setup-ollie-image" />
				</div>
				{progressRows}
			</div>
		</ClaimJurisdictionCard>
	);

	const underReviewCard = (
		<ClaimJurisdictionCard
			key={ClaimSetupState.UNDER_REVIEW}
			eyebrowIcon={<ShieldCheck size={16} />}
			eyebrow="UNDER REVIEW"
			title="We’re reviewing your claim."
			description={`Your email is verified. Our team will confirm that you represent ${jurisdictionName} and email you when your access is ready.`}
		/>
	);

	const rejectedCard = (
		<ClaimJurisdictionCard
			key={ClaimSetupState.REJECTED}
			eyebrowIcon={<ShieldCheck size={16} />}
			eyebrow="CLAIM NOT APPROVED"
			title="We couldn’t approve this claim."
			description={`We could not confirm that you represent ${jurisdictionName}. If you think this is a mistake, contact support@highnoteai.com.`}
		/>
	);

	const failedCard = (
		<ClaimJurisdictionCard
			key={ClaimSetupState.FAILED}
			eyebrowIcon={<CircleAlert size={16} />}
			eyebrow="SOMETHING WENT WRONG"
			title="We couldn’t finish your setup."
			description={errorMessage}
		>
			<ClaimJurisdictionButton variant="primary" onClick={() => navigate(routeNames.CLAIM_JURISDICTION)}>
				Back to the claim page
			</ClaimJurisdictionButton>
		</ClaimJurisdictionCard>
	);

	const cardBySetupState = {
		[ClaimSetupState.PREPARING]: preparingCard,
		[ClaimSetupState.UNDER_REVIEW]: underReviewCard,
		[ClaimSetupState.REJECTED]: rejectedCard,
		[ClaimSetupState.FAILED]: failedCard
	};

	return (
		<ClaimJurisdictionLayout title="A fresh start for your jurisdiction.">
			<div className="claim-jurisdiction-setup">{cardBySetupState[setupState]}</div>
		</ClaimJurisdictionLayout>
	);
};

export default ClaimJurisdictionSetup;
